<template>
  <template v-if="question?.status !== 'READ_ONLY'">
    <div
        tabindex="0"
        @click="shouldPlaceholderWrap ? () => {if(!isMenuOpened) {changeMenuVisibility(true)} else {changeMenuVisibility(false)}} : ''"
        @focusin.stop="() => {isFocused = true; window.setTimeout(() => {if(!isMenuOpened) changeMenuVisibility(true)},500);}"
        class="inputContainer input-border"
        :class="{'border-setup': !hasBeenInteractedWith, 'container-menu-opened': isMenuOpened, 'inputContainer-focus': !isFocused && shouldPlaceholderWrap, 'container-height': inputTempValue && shouldPlaceholderWrap }"
        role="combobox"
        @focusout="changeMenuVisibility(false); isFocused= false"
        aria-haspopup="listbox"
    >
    <input
          style="outline: none;"
          v-bind="$attrs"
          aria-label="Select an option"
          :aria-expanded="isMenuOpened"
          :aria-controls="itemsListId"
          class="quill-question-input choice"
          @click="changeMenuVisibility(!isMenuOpened)"
          :class="{ invalid: error && hasBeenInteractedWith, adjust: inputTempValue || isFocused, 'cursor-pointer': disableFiltering, 'position-absolute':!isFocused && shouldPlaceholderWrap, 'quill-question-input-focused': !isFocused && shouldPlaceholderWrap }"
          :readonly="disableFiltering"
          ref="autoCompleteInput"
          type="text"
          :name="`waffle_${question?.name}`"
          v-model="inputTempValue"
          :disabled="disabled"
          @blur="handleFocusOut; isFocused= false"
          @keydown.stop="handleKeyDown"
          @focusin.stop="() => {isFocused = true; window.setTimeout(() => {if(!isMenuOpened) changeMenuVisibility(true)},500);}"
          @input="handleInput"
      />
      <span class="quill-question-placeholder d-inline-block text-break" :class="{'placeholder-wrap':isFocused  || inputTempValue, 'position-relative':!isFocused && !inputTempValue && shouldPlaceholderWrap}">
      {{
          forcePromptPlaceholder
              ? question?.prompt
              : question?.placeholder
      }}
    </span>
      <ul :id="itemsListId" role="listbox" ref="suggestionList" class="user-select-none">
        <li v-if="loadingItems">loading items...</li>
        <li
            class="noOptions"
            role="option"
            v-else-if="!filteredItems.length"
            disabled
            @click.stop.prevent="() => {}"
        >
          No options found
        </li>
        <li
            role="option"
            :aria-selected="isSelected(option)"
            v-for="(option, index) in filteredItems"
            :key="index"
            @mousedown.stop.prevent="setChoiceValue(option)"
            @focusout.stop="() => {}"
            :class="{
          selected: isSelected(option),
        }"
        >
          {{ getItemText(option) }}
        </li>
      </ul>
      <div class="ms-4">
        <i class="me-2" @click="changeMenuVisibility(!isMenuOpened)" :style="{ top: (isFocused || (!isFocused && inputTempValue)  ? '27%' : '43%'), transform: !isFocused && !inputTempValue ? 'translate(0, -50%)' : '' }">
          <svg color="#757575" v-if="isMenuOpened" width="24" height="24"><use href="../../../../../public/sprite.svg#chevron-up"/></svg>
          <svg color="#757575" v-else width="24" height="24"><use href="../../../../../public/sprite.svg#chevron-down"/></svg>
        </i>
      </div>
    </div>
    <slot name="helptext" :isError="error" :isConfirm="!disableConfirm && (question?.value && question?.prefilled) && !error"
          :confirmCb="() => {$emit('user-interaction'); $emit('confirm')}"></slot>
  </template>
  <ReadOnly
      v-else
      :question="question"
      :value="this.inputTempValue ? `${this.inputTempValue}`:'-'"
      :forcePromptPlaceholder="forcePromptPlaceholder"
  />
</template>

<script>
//TODO: IF LIST SUFFERS FROM RENDER SLOWDOWN DUE TO SIZE, IMPLEMENT VIRTUALIZATION TO SPEED IT UP https://vueuse.org/core/useVirtualList/ , https://github.com/Akryum/vue-virtual-scroller/blob/master/packages/vue-virtual-scroller/README.md --> TWO GOOD OPTIONS
import ReadOnly from "@/components/waffle/components/form-components/ReadOnly.vue";

export default {
  name: "AutoCompleteInput",
  components: {ReadOnly},
  props: {
    disableConfirm:Boolean,
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    question: {
      type: Object,
      required: true,
    },
    itemText: {
      type: String,
      required: false,
    },
    returnObject: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    itemValue: {
      type: String,
      required: false,
    },
    loadingItems: {
      type: Boolean,
      required: false,
    },
    modelValue: {
      type: [String, Object],
      required: false,
    },
    disableFiltering: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    hideSubText: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    forcePromptPlaceholder: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    skipEagerValidation: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    shouldPlaceholderWrap: {
      type: Boolean,
      required: true,
    },
    hasBeenInteractedWith:Boolean
  },
  data() {
    return {
      inputTempValue: "",
      selectedValue: null,
      isTyping: false,
      error: "",
      isMenuOpened: false,
      itemsListId: `id_${Math.random().toString(36).substr(2, 13)}`, // random id
      isFocused: false
    };
  },
  mounted() {
    this.selectedValue = (this.question?.options || []).find(
        (item) => item[this.bindingProp] === this.question?.value
    );
    this.inputTempValue = this.selectedValue
        ? this.selectedValue[this.bindingDisplay]
        : "";
    if (
        this.question?.error ||
        (this.question?.required &&
            !this.selectedValue &&
            !this.skipEagerValidation)
    ) {
      this.error = this.question?.error || "Required";
      return this.$emit("field-error", true);
    }
  },
  computed: {
    bindingDisplay() {
      return this.itemText || "text";
    },
    bindingProp() {
      return this.itemValue || "value";
    },
    frontendCriteria() {
      return this.question?.criteria?.find(el => typeof el === 'object') ?? {}
    },
    filteredItems() {
      const itemIsObject =
          this.question?.options && typeof this.question?.options[0] === "object";
      if ((['*', '+', '{', '}']).includes(this.inputTempValue)) {
        return this.question?.options
      }

      return (this.question?.options || []).filter((item) => {
        if (!this.inputTempValue || this.disableFiltering || !this.isTyping) {
          return true;
        }
        return itemIsObject
            ? item[this.bindingDisplay].toLowerCase().startsWith(this.inputTempValue.toLowerCase())
            : item.toLowerCase().startsWith(this.inputTempValue.toLowerCase())
      });
    },
  },
  emits: ["update:modelValue", "field-error", 'confirm','user-interaction'],
  watch: {
    inputTempValue(newVal) {
        if (!newVal && this.question?.required) {
          this.error = "Required";
          this.$emit("field-error", true);
        } else {
          this.error = "";
          this.$emit("field-error", false);
        }
    },
    question: {
      handler(newQuestion) {
        if (newQuestion.error) {
          this.error = newQuestion.error;
          return this.$emit("field-error", true);
        }

        this.selectedValue = (newQuestion.options || []).find(
            (item) => item[this.bindingProp] === this.question?.value
        );

        const returnVal = this.returnObject
            ? this.selectedValue
            : (this.selectedValue || {})[this.bindingProp];
        if(this.question.value !== returnVal){
          this.$emit("update:modelValue", this.question.value);
        }

          if(returnVal && newQuestion.required){
            this.error = '';
            this.$emit("field-error", false);
          }else{
            this.error = 'Required';
          }

        this.inputTempValue = this.selectedValue?.[this.bindingDisplay] ?? '';
      },
      deep: true,
    },
  },
  methods: {
    changeMenuVisibility(value) {
      this.isMenuOpened = value;
    },
    handleKeyDown(e) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const currentIndex = this.filteredItems.findIndex(this.isSelected);
        if (currentIndex === -1) {
          this.setChoiceValue(this.filteredItems[0], "key");
        } else {
          const totalItems = this.filteredItems.length;
          let nextIndex =
              (currentIndex + (e.key === "ArrowDown" ? 1 : -1)) % totalItems;
          if (nextIndex < 0) {
            nextIndex = totalItems - 1;
          }
          this.setChoiceValue(this.filteredItems[nextIndex], "key");

          // Scroll to the focused item
          this.scrollIntoView(nextIndex);
        }
      } else if (e.key === "Enter" || e.key === 'Tab') {
        const currentIndex = this.filteredItems.findIndex(this.isSelected);
        if (currentIndex === -1) {
          this.setChoiceValue(null, "key");
        } else {
          this.setChoiceValue(this.filteredItems[currentIndex]);

          const totalItems = this.filteredItems.length;
          let nextIndex =
              (currentIndex + (e.key === "ArrowDown" ? 1 : -1)) % totalItems;
          if (nextIndex < 0) {
            nextIndex = totalItems - 1;
          }
          // Scroll to the focused item
          this.scrollIntoView(nextIndex);
        }
      }
    },

    // New method to scroll to the focused item
    scrollIntoView(index) {
      const focusedItem = this.$refs.suggestionList.children[index];
      if (focusedItem) {
        focusedItem.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    },
    isSelected(option) {
      return (
          this.inputTempValue === (option.text || option[this.itemText] || option)
      );
    },

    getItemText(option) {
      return this.itemText
          ? option[this.itemText]
          : typeof option === "object"
              ? option.text
              : option;
    },

    handleInput(e) {
      if (
          this.selectedValue &&
          this.selectedValue[this.bindingDisplay] !== e.target.value
      ) {
        this.selectedValue = null;
        // this.isTyping= false
        // return
      }
      if (this.frontendCriteria?.regex) {
        const regex = new RegExp(this.frontendCriteria?.regex, 'g')
        this.inputTempValue = this.inputTempValue?.replace(regex, "$1")
      }
      this.changeMenuVisibility(true);
      this.isTyping = true;
    },
    handleFocusOut() {
      this.isMenuOpened = false
      if (!this.filteredItems.length) {
        this.inputTempValue = ""
        return;
      }
      this.changeMenuVisibility(false);
      setTimeout(() => {
        if (!this.selectedValue) {
          this.inputTempValue = "";
        }
      }, 250);
    },
    emitResult() {
      this.$emit('user-interaction')
      const returnVal = this.returnObject
          ? this.selectedValue
          : (this.selectedValue || {})[this.bindingProp];
      this.$emit("update:modelValue", this.selectedValue ? returnVal : null);
    },
    setChoiceValue(option, k) {
      this.isTyping = false;
      this.selectedValue = option || null;
      this.inputTempValue = (option || {})[this.bindingDisplay] || null;

      if (k !== "key") {
        this.changeMenuVisibility(false);
      }
      this.emitResult()
    },
  },
};
</script>

<style scoped lang="scss">
.container-height {
  height: 51px;
}

.align-text-end {
  text-align: end;
}

.choice {
  border:none;
  text-overflow: ellipsis;
}

.noOptions {
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.subtext {
  font-size: 0.75rem;
  font: normal normal normal 12px/18px Roboto;
  letter-spacing: 0px;
  margin-bottom: 4px;
  color: #757575;
}

.errorMessage {
  color: #bb0000;
}

.quill-question {
  > div {
    position: relative;
    cursor: pointer;

    &:hover:not(.invalid)::after, &:focus:not(.invalid)::after, &:focus-within:not(.invalid)::after, &:focus-visible:not(.invalid)::after   {
        content: '';
        position: absolute;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        border: 2px solid #212121;
        border-radius: 3px;
        pointer-events: none;
        outline: none;
        box-shadow: none;
      }
    > .quill-question-input-focused{
      border: none !important;
      &:focus{
        outline: auto !important;
      }
    }
    > .quill-question-input {
      box-sizing: border-box;
      border: 1px solid #ccc;
      border-radius: 3px;
      padding: 8px 32px 12px;
      width: 100%;
      font-size: 16px;
      background: transparent;
      height: 50px;
      font-weight: bold;
      cursor: pointer;
    }

    .quill-question-placeholder {
      position: absolute;
      padding: 10px 16px;
      padding-right: 32px !important;
      line-height: 2em;
      top: 0;
      left: 0;
      color: #757575;
      font-size: 16px;
      pointer-events: none;
      transform: scale(1);
      transition: 0.26s all ease;
    }

    > ul {
      list-style: none;
      padding: 0;
      position: absolute;
      top: calc(100% - 1px);
      background: white;
      border: 1px solid #21212122;
      box-shadow: 3px 3px 10px #21212122;
      width: 100%;
      z-index: 9;
      visibility: hidden;
      transition-delay: 0.13s;
      max-height: 200px;
      overflow: auto;

      > li {
        padding: 2px 15px;
        cursor: pointer;

        &:hover {
          font-weight: bold;
          color: #212121;
          background: #f8f8f8;
        }

        &.selected {
          font-weight: bold;
          color: #212121;
          background: #ededed;
        }
      }
    }

    > div > i {
      content: "";
      display: block;
      width: 12px;      
      // top: 12.5px;
      height: 12px;
      position: absolute;
      right: 12px;
      color: #525252;
      transition: 0.26s all ease;
    }
  }

  > b {
    color: red;
    display: none;
  }

  &.error {
    > b {
      display: block;
    }

    > div {
      border-color: red;
    }
  }

  > u {
    display: block;
    text-align: right;
    text-decoration: underline;
    cursor: pointer;
  }
}

/*TODO convert to scss*/

.quill-question > label > div > i > svg:hover:hover ~ span {
  visibility: visible;
  transition-delay: 0.31s;
}

div.inputContainer > .quill-question-input:focus-visible{
  outline:2px auto #212121;
}

.quill-question > div.inputContainer .quill-question-input:focus,
.quill-question > div.inputContainer .quill-question-input:not(.radio, :placeholder-shown) {
  padding: 24px 8px 8px 12px;
}

.quill-question > div.inputContainer .quill-question-input:focus + .quill-question-placeholder,
.quill-question > div.inputContainer .quill-question-input:not(.radio, :placeholder-shown) + .quill-question-placeholder {
  font-size: 16px;
  padding: 10px 12px;
}

.quill-question > div.inputContainer.container-menu-opened > ul {
  /*display: block;*/
  visibility: visible;
  transition-delay: 0.1s;
}

.inputContainer-focus {
  border: 1px solid #ccc;
  border-radius: 3px;
}

// Start Floating Label CSS
.quill-question > div.inputContainer .quill-question-input:focus + .quill-question-placeholder {
  font-size: 12px;
  padding: 4px 8px 5px 12px;
}

.adjust + .quill-question-placeholder {
  font-size: 12px !important;
  padding: 3px 8px 5px 12px !important;
}

.form-floating > .form-control:focus ~ label.form-label-text, .form-floating > .form-control:focus ~ label {
  font-size: 12px;
  color: #484848;
  padding-top: 1rem;
}

.form-control-disabled:hover {
  border: none;
}

.form-control-enabled:hover {
  border: 2px solid #212121
}

// End Floating Label CSS

.invalid {
  border: 2px solid #bb0000 !important;

  &:hover,
  &:focus,
  &:focus-within {
    border: 2px solid #bb0000 !important;
  }
}
.cursor-pointer {
  cursor: pointer;
}
</style>
